import { useReducedMotion } from '#pplx/frontend/common/hooks/useReducedMotion.js';
import { COMPOSER_MOTION_SPRING } from '#pplx/frontend/composer-sdk/composerMotion.js';
import { useSpring } from '@react-spring/web';
import { useCallback, useEffect, useRef, useState } from 'react';
const TOGGLE_SPRING_CONFIG = {
    tension: 700,
    friction: 50,
    clamp: true,
};
function isToggleGroupVisible(element) {
    if (!element)
        return false;
    // Engines without checkVisibility (older Safari, happy-dom/jsdom) fall back
    // to enabling animations on the first frame, matching prior behavior.
    if (typeof element.checkVisibility !== 'function')
        return true;
    return element.checkVisibility({
        checkOpacity: true,
        checkVisibilityCSS: true,
        opacityProperty: true,
        visibilityProperty: true,
    });
}
export function useModeToggleAnimation({ activeIndex, activeX, activeWidth, layoutWidth, hasActiveToggle, persistentLabels, animateWidth, framed, }) {
    const reducedMotion = useReducedMotion();
    const springConfig = framed ? COMPOSER_MOTION_SPRING : TOGGLE_SPRING_CONFIG;
    const containerRef = useRef(null);
    const [indicatorAnimationEnabled, setIndicatorAnimationEnabled] = useState(false);
    const [isActiveToggleSwitchAnimating, setIsActiveToggleSwitchAnimating] = useState(false);
    const previousActiveIndexRef = useRef(activeIndex);
    const previousActiveToggleXRef = useRef(activeX);
    const activeToggleChanged = previousActiveIndexRef.current !== activeIndex;
    const activeTogglePositionChanged = previousActiveToggleXRef.current !== activeX;
    // Only variants with a wider active toggle need switch animation bookkeeping:
    // active labels in the default layout, or icon + chevron in icon-only layout.
    const shouldTrackSwitchAnimation = animateWidth && indicatorAnimationEnabled && activeToggleChanged && activeTogglePositionChanged;
    // Side switches need the measured width spring. Same-side mode changes can use
    // layout width to avoid a stale measurement frame.
    const shouldAnimateActiveToggleWidth = animateWidth && (shouldTrackSwitchAnimation || isActiveToggleSwitchAnimating);
    const shouldUseIndicatorLayoutWidth = !persistentLabels && !shouldAnimateActiveToggleWidth;
    const handleActiveToggleSwitchAnimationRest = useCallback(() => {
        setIsActiveToggleSwitchAnimating(false);
    }, []);
    const indicatorPositionSpring = useSpring({
        opacity: hasActiveToggle ? 1 : 0,
        x: hasActiveToggle ? activeX : 0,
        immediate: reducedMotion || !indicatorAnimationEnabled,
        config: springConfig,
        onRest: handleActiveToggleSwitchAnimationRest,
    });
    const indicatorWidthSpring = useSpring({
        width: hasActiveToggle ? activeWidth : 0,
        immediate: reducedMotion ||
            !indicatorAnimationEnabled ||
            (!persistentLabels && !shouldAnimateActiveToggleWidth),
        config: springConfig,
    });
    // Arm animations only once the control is actually visible, so callers that
    // initially hide the input never animate the first resolved layout. A
    // consumer that stays hidden indefinitely would keep polling one rAF per
    // frame; no such surface exists today — switch to IntersectionObserver if one
    // appears.
    useEffect(() => {
        let id;
        const enableWhenVisible = () => {
            if (!isToggleGroupVisible(containerRef.current)) {
                id = window.requestAnimationFrame(enableWhenVisible);
                return;
            }
            // One more frame so the first visible layout commits without animation.
            id = window.requestAnimationFrame(() => setIndicatorAnimationEnabled(true));
        };
        id = window.requestAnimationFrame(enableWhenVisible);
        return () => window.cancelAnimationFrame(id);
    }, []);
    useEffect(() => {
        previousActiveIndexRef.current = activeIndex;
        previousActiveToggleXRef.current = activeX;
        if (shouldTrackSwitchAnimation) {
            setIsActiveToggleSwitchAnimating(true);
            return;
        }
        if (!activeToggleChanged)
            return;
        setIsActiveToggleSwitchAnimating(false);
    }, [activeToggleChanged, activeIndex, activeX, shouldTrackSwitchAnimation]);
    return {
        containerRef,
        indicatorStyle: {
            insetInlineStart: framed
                ? indicatorPositionSpring.x.to((x) => x + 2)
                : indicatorPositionSpring.x,
            opacity: indicatorPositionSpring.opacity,
            width: shouldUseIndicatorLayoutWidth ? layoutWidth : indicatorWidthSpring.width,
        },
        widthAnimationProps: {
            animationEnabled: indicatorAnimationEnabled && !reducedMotion,
            shouldAnimateWidth: shouldAnimateActiveToggleWidth,
            springConfig,
        },
    };
}
//# sourceMappingURL=useModeToggleAnimation.js.map